import React, { useMemo, useState } from 'react';
import { getMaxRotateForImage } from './decalEngine';

/**
 * User-editable image array for Computer Parts (microchips, RAM sticks, circuit boards, LEDs).
 * Clean root-relative URLs pointing to local artwork assets.
 */
export const COMPUTER_PART_IMAGES: (string | { src: string; maxRotate?: number })[] = [
  '/Images/Computer Parts/ram.png',
  '/Images/Computer Parts/board.png',
  '/Images/Computer Parts/microchip.png',
  '/Images/Computer Parts/leds.png',
  '/Images/Computer Parts/battery.png'
];

export interface ComputerPartProps {
  x?: number; // percentage (0 - 100)
  y?: number; // percentage (0 - 100)
  rotation?: number; // degrees
  /** Custom +/- max rotation clamp (0 - 360). Defaults to 360 (full rotation) */
  maxRotate?: number;
  scale?: number;
  opacity?: number;
  imageSrc?: string;
  images?: (string | { src: string; maxRotate?: number })[];
  dropShadow?: string;
  size?: number | string;
  className?: string;
  style?: React.CSSProperties;
}

export const ComputerPart: React.FC<ComputerPartProps> = ({
  x = 50,
  y = 50,
  rotation = 0,
  maxRotate,
  scale = 1,
  opacity = 1,
  imageSrc,
  images,
  dropShadow,
  size = 130,
  className = '',
  style
}) => {
  const pool = images && images.length > 0 ? images : COMPUTER_PART_IMAGES;
  const chosen = useMemo(() => {
    if (imageSrc) return { src: imageSrc, maxRotate };
    const idx = Math.floor(Math.random() * pool.length);
    const item = pool[idx];
    if (typeof item === 'string') {
      return { src: item, maxRotate };
    }
    return { src: item.src, maxRotate: item.maxRotate ?? maxRotate };
  }, [imageSrc, pool, maxRotate]);

  const [currentSrc, setCurrentSrc] = useState(chosen.src);

  // Dedicated hardware drop shadow logic contouring transparent PNG wafer
  const techFilter = dropShadow || 'drop-shadow(2px 3px 5px rgba(0, 0, 0, 0.45))';

  // Hardware rotation logic: defaults to 360 (full rotation), or custom maxRotate
  const effectiveMaxRotate = useMemo(() => {
    return getMaxRotateForImage(currentSrc, chosen.maxRotate ?? maxRotate, 'tech');
  }, [currentSrc, chosen.maxRotate, maxRotate]);

  const normalized = ((rotation % 360) + 540) % 360 - 180;
  const clampedRotation = effectiveMaxRotate < 360
    ? Math.max(-effectiveMaxRotate, Math.min(effectiveMaxRotate, normalized))
    : rotation;

  return (
    <div
      className={`random-thing decal-tech ${className}`}
      style={{
        position: 'absolute',
        left: `${x}%`,
        top: `${y}%`,
        transform: `translate(-50%, -50%) rotate(${clampedRotation}deg) scale(${scale})`,
        opacity: 1,
        pointerEvents: 'none',
        userSelect: 'none',
        mixBlendMode: 'normal',
        filter: techFilter,
        ...style
      }}
    >
      <img
        src={currentSrc}
        alt="computer part"
        style={{
          display: 'block',
          width: typeof size === 'number' ? `${size}px` : size,
          height: 'auto',
          pointerEvents: 'none'
        }}
        onError={() => {
          if (!currentSrc.startsWith('/') && !currentSrc.startsWith('http')) {
            setCurrentSrc(`/${currentSrc}`);
          }
        }}
      />
    </div>
  );
};

export default ComputerPart;
